/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {Fragment, useCallback, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import InputAdornment from '@material-ui/core/InputAdornment';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {CVSAssetType} from '@ion/core/src/types/entities';
import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
import Divider from '@ion/dls/src/components/divider/Divider';
import TextField from '@ion/dls/src/components/textfield/TextField';
import {
  MaterialExitCloseIcon
} from '@ion/dls/src/icons/MaterialExitCloseIcon';
import {
  TopNavSearchIcon
} from '@ion/dls/src/icons/TopNavSearchIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {LabelTypography} from '@ion/dls/src/typography/LabelTypography';
import {search} from '@/store/actions/search';
import {
  assetsSearchSelector,
  driversSearchSelector,
  geofencesSearchSelector,
  loadingSearchSelector,
  searchTermSearchSelector
} from '@/store/selectors/search-selectors';
import SearchResult from './SearchResult';
import styles from './MobileHeader.module.scss';

export interface MobileHeaderSearch {
  onSearch?: Function;
  isSearching?: boolean;
  searchTerm?: string;
  searchResults?: CVSAssetType[];
  handleClickOpen?: () => void;
  dialogOpen?: boolean;
  setDialogOpen?: Function
}
const useStyles = makeStyles((theme) => ({
  root : {
    margin  : 0,
    padding : '16px'
  },
  paper : {
    maxWidth     : '100%',
    margin       : '0',
    background   : dlsColor('white'),
    border       : '1px solid',
    borderColor  : dlsColor('grey5'),
    borderRadius : 0.5
  },
  scrollPaper : {
    alignItems : 'baseline'
  },
  suggestedText : {
    marginLeft   : '20px',
    marginBottom : '2%'
  },
  dialogContent : {
    padding   : '0px !important',
    marginTop : '-1%'
  },
  loader : {
    textAlign : 'center',
    padding   : '2%'
  }
}));

/**
 * @MobileHeaderSearch
 *
 * The Mobile Header Search component provides the application Header
 * Search for the iOn Application
 * when view in Mobile Mode.
 */
export const MobileHeaderSearch = () => {
  const classes  = useStyles();
  const dispatch = useDispatch();
  const [clearcontent, setClearContent] = useState(false);
  const [dialogOpen, setDialogOpen]     = useState(false);
  const searchedAssets    = useSelector(assetsSearchSelector) || [];
  const searchedDrivers   = useSelector(driversSearchSelector) || [];
  const searchedGeofences = useSelector(geofencesSearchSelector) || [];
  const isSearching       = useSelector(loadingSearchSelector);
  const searchTerm        = useSelector(searchTermSearchSelector);

  const handleClickOpen = useCallback(function() {
    setDialogOpen(true);
  }, []);

  const onSearch = useCallback(function(searchTerm) {
    dispatch(search(searchTerm));
  }, []);

  function setSearchTerm(e) {
    const search = e.target.value;
    if (search!== '' && search.length > 2) {
      setClearContent(false);
      onSearch(search);
    } else {
      setClearContent(true);
    }
  }

  function handleClose() {
    setDialogOpen(false);
    setClearContent(true);
  };

  const searchResults = useMemo(() => {
    return [...searchedAssets, ...searchedDrivers, ...searchedGeofences];
  }, [searchedAssets, searchedDrivers, searchedGeofences]);

  return (
    <div style={{visibility: 'hidden'}}>
      <div className={styles.leftContent}>
        <div className={styles.leftSubContent}>
          <IconButton color='inherit'
            disableRipple
            onClick={handleClickOpen}
            icon={<TopNavSearchIcon />} />
        </div>
      </div>
      <div>
        <Dialog aria-labelledby='customized-dialog-title' open={dialogOpen}
          classes={{scrollPaper: classes.scrollPaper, paper: classes.paper, root: classes.root}}>
          <DialogTitle id='customized-dialog-title'>
            <TextField label='' placeholder={translate('nav:header.search')} style={{bottom: '10px'}}
              onChange={setSearchTerm}
              InputProps={{
                startAdornment : (
                  <InputAdornment position='start' style={{cursor: 'pointer', position: 'relative', right: '16px', marginRight: '0px'}}>
                    <IconButton disableRipple
                      disabled={isSearching}
                      icon={<TopNavSearchIcon />} />
                  </InputAdornment>
                ),
                endAdornment : (
                  <InputAdornment position='end' style={{cursor: 'pointer', position: 'relative', left: '27px'}}>
                    <IconButton disableRipple onClick={handleClose}
                      icon={<MaterialExitCloseIcon />} />
                  </InputAdornment>
                )
              }} />
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            {!clearcontent
              ? (
                isSearching
                  ? (<div className={classes.loader}>{translate('common:states.loading')}</div>)
                  : dialogOpen && (
                    <>
                      <div className={classes.suggestedText}>
                        <LabelTypography>
                          {searchTerm
                            ? searchResults?.length + ' Results for ' + searchTerm
                            : translate('common:search.searchItems')}
                        </LabelTypography>
                      </div>
                      <div>
                        {searchResults?.map((data, index) => (
                          <Fragment key={`search-${index}`}>
                            <SearchResult result={data}
                              searchTerm={searchTerm}
                              setDialogOpen={setDialogOpen} />
                            <Divider />
                          </Fragment>
                        ))}
                      </div>
                    </>)
              ) : (
                <div className={classes.suggestedText}>
                  <LabelTypography>
                    {translate('common:search.searchItems')}
                  </LabelTypography>
                </div>
              )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default React.memo(MobileHeaderSearch);
